import React, { useEffect, useRef, useState } from "react";
import { motion, useInView, animate } from "framer-motion";

import { color } from "@/lib/theme";

export default function TrustScore({ value = 87, size = 220, label = "Meira Trust Score", tone = "gold" }) {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, amount: 0.5 });
  const [display, setDisplay] = useState(0);
  const width = 8;
  const r = (size - width * 2) / 2;
  const c = 2 * Math.PI * r;
  const pct = value / 100;

  // Gold by default: a trust score is a proof moment, one of the few places
  // the palette allows it.
  const token = tone === "gold" ? "gold" : tone === "valid" ? "valid" : "brand";

  useEffect(() => {
    if (!inView) return;
    const controls = animate(0, value, {
      duration: 1.6,
      ease: [0.16, 1, 0.3, 1],
      onUpdate: (v) => setDisplay(Math.round(v)),
    });
    return () => controls.stop();
  }, [inView, value]);

  return (
    <div ref={ref} className="flex flex-col items-center" style={{ width: size }}>
      <div className="relative" style={{ width: size, height: size }}>
        <svg width={size} height={size} className="-rotate-90">
          <circle cx={size / 2} cy={size / 2} r={r} fill="none" stroke={color("line")} strokeWidth={width} />
          <motion.circle
            cx={size / 2}
            cy={size / 2}
            r={r}
            fill="none"
            stroke={color(token)}
            strokeWidth={width}
            strokeLinecap="round"
            strokeDasharray={c}
            initial={{ strokeDashoffset: c }}
            animate={inView ? { strokeDashoffset: c - c * pct } : {}}
            transition={{ duration: 1.6, ease: [0.16, 1, 0.3, 1] }}
            style={{ filter: `drop-shadow(0 0 6px ${color(token, 0.35)})` }}
          />
        </svg>
        <div className="absolute inset-0 flex flex-col items-center justify-center">
          <span className="font-display font-light leading-none text-content tabular-nums" style={{ fontSize: Math.round(size * 0.3) }}>{display}</span>
          <span className="font-mono tracking-[0.2em] text-content-faint" style={{ fontSize: Math.max(8, Math.round(size * 0.05)) }}>/ 100</span>
        </div>
      </div>
      <span className="mt-3 text-center font-mono text-[9px] uppercase leading-tight tracking-[0.2em] text-content-muted sm:mt-4 sm:text-[10px] sm:tracking-[0.24em]">{label}</span>
    </div>
  );
}

export function ScoreBar({ label, value, tone = "brand", delay = 0 }) {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, amount: 0.6 });
  const fill = color(tone === "gold" ? "gold" : tone === "valid" ? "valid" : "brand");
  return (
    <div ref={ref} className="flex flex-col gap-2">
      <div className="flex items-baseline justify-between">
        <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-content-muted">{label}</span>
        <span className="font-mono text-sm text-content">{value}%</span>
      </div>
      <div className="h-1 w-full overflow-hidden rounded-full bg-line">
        <motion.div
          className="h-full rounded-full"
          style={{ background: fill }}
          initial={{ width: 0 }}
          animate={inView ? { width: `${value}%` } : {}}
          transition={{ duration: 1.2, ease: [0.16, 1, 0.3, 1], delay }}
        />
      </div>
    </div>
  );
}
